// web/src/components/StatTile.tsx
// Single-stat display. Big numeral, small-caps label. Optional sub-line
// below. On mount, the value counts up — uses framer-motion. Falls back to
// static rendering when value is a string (e.g. "—" or "9/10").

import { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import type { CSSProperties, ReactNode } from 'react';

export interface StatTileProps {
  /** Numeric value counts up; string value renders as-is. */
  value: number | string;
  label: string;
  sub?: string;
  /** Highlight colour for the value. Defaults to var(--ink). */
  accent?: string;
  /** Override the default count-up animation. */
  animate?: boolean;
  /** Stagger delay (sec) when used inside a row of tiles. */
  delay?: number;
  /** Optional decorative icon shown beside the label. */
  icon?: ReactNode;
  style?: CSSProperties;
}

function useCountUp(target: number, duration: number, enabled: boolean, delay: number): number {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v: number) => Math.round(v));
  const [display, setDisplay] = useState<number>(enabled ? 0 : target);

  useEffect(() => {
    if (!enabled) { setDisplay(target); return; }
    const controls = animate(motionValue, target, {
      duration,
      delay,
      ease: [0.22, 1, 0.36, 1],
    });
    const unsubscribe = rounded.on('change', (v: number) => setDisplay(v));
    return () => { controls.stop(); unsubscribe(); };
  }, [target, duration, delay, enabled, motionValue, rounded]);

  return display;
}

export function StatTile({
  value,
  label,
  sub,
  accent = 'var(--ink)',
  animate: shouldAnimate = true,
  delay = 0,
  icon,
  style,
}: StatTileProps) {
  const isNumeric = typeof value === 'number';
  const displayed = useCountUp(isNumeric ? value : 0, 0.7, shouldAnimate && isNumeric, delay);

  return (
    <motion.div
      initial={shouldAnimate ? { opacity: 0, y: 8 } : undefined}
      animate={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-base)',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-xs)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--micro)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 'var(--space-xs)',
        }}
      >
        {icon ? <span style={{ display: 'inline-flex' }}>{icon}</span> : null}
        <span>{label}</span>
      </div>
      <div
        className="mfj-tnum"
        style={{
          fontFamily: 'var(--font-masthead)',
          // Auto-shrink for long string values (e.g. "1/0/0", "9/10"). The
          // --display scale (2.75-5rem) easily overflows a 120px tile when the
          // value is wider than 3 characters.
          fontSize: typeof value === 'string' && value.length > 3
            ? 'clamp(1.75rem, 5vw, 2.5rem)'
            : 'var(--display)',
          lineHeight: 0.95,
          color: accent,
          fontStyle: 'italic',
          // Last-ditch — if it still doesn't fit, ellipsis kicks in
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'clip',
        }}
      >
        {isNumeric ? displayed : value}
      </div>
      {sub ? (
        <div style={{ marginTop: 'var(--space-xs)', fontSize: 'var(--small)', color: 'var(--muted)' }}>
          {sub}
        </div>
      ) : null}
    </motion.div>
  );
}
